import { i18n } from 'utils';
import { useAppSelector } from 'app/hooks';

export type PbxStatus = 'UP' | 'RECOVERING' | 'DOWN' | 'NO_PBX' | null;

/**
 * True si NO se pueden cursar salientes. Unica fuente de verdad de esa lista de
 * estados: la usan el hook de los botones y los guards de los thunks, que antes
 * repetian la condicion cada uno por su cuenta.
 *
 * NO_PBX entra aqui (el nodo corre sin FreeSWITCH, no hay telefonia) aunque NO es
 * una averia: los banners de aviso se pintan solo para DOWN/RECOVERING.
 */
export const callsDisabledFor = (pbxStatus: PbxStatus): boolean =>
  pbxStatus === 'DOWN' || pbxStatus === 'RECOVERING' || pbxStatus === 'NO_PBX';

/**
 * Devuelve el estado de conexion con la centralita (pbxStatus del poll /lastUpdate).
 *
 * SOLO EN DESARROLLO admite forzar el valor por query-param para probar los banners
 * sin tener que romper el ESL real:  ?pbxDebug=DOWN | RECOVERING | UP | NO_PBX
 * En el build de produccion (NODE_ENV === 'production') el override se ignora.
 */
export const usePbxStatus = (): PbxStatus => {
  const real = useAppSelector((state: any) => state.alarmQueue?.pbxStatus) ?? null;

  if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    // El query-param setea un override que PERSISTE en sessionStorage, para que
    // sobreviva a la navegacion del SPA (al ir a la ficha de usuario la URL cambia
    // y se perdia el ?pbxDebug). Se limpia con ?pbxDebug=OFF.
    const fromUrl = new URLSearchParams(window.location.search).get('pbxDebug');
    if (fromUrl) {
      if (fromUrl.toUpperCase() === 'OFF') {
        window.sessionStorage.removeItem('pbxDebug');
      } else {
        window.sessionStorage.setItem('pbxDebug', fromUrl.toUpperCase());
      }
    }
    const dbg = window.sessionStorage.getItem('pbxDebug');
    if (dbg === 'UP' || dbg === 'RECOVERING' || dbg === 'DOWN' || dbg === 'NO_PBX') {
      return dbg as PbxStatus;
    }
  }

  return real as PbxStatus;
};

/** True si NO se deben cursar llamadas salientes. Para deshabilitar los botones. */
export const useCallsDisabled = (): boolean => callsDisabledFor(usePbxStatus());

/**
 * Texto del tooltip de los botones de llamada deshabilitados ('' si estan activos).
 * Distingue la averia (DOWN/RECOVERING: "sin conexion con la central") del servicio
 * que sencillamente no tiene telefonia (NO_PBX), que no es nada que reportar.
 */
export const useCallsDisabledReason = (): string => {
  const pbxStatus = usePbxStatus();
  if (!callsDisabledFor(pbxStatus)) return '';
  return i18n(pbxStatus === 'NO_PBX' ? 'pbx.status.callsDisabled.noPbx' : 'pbx.status.callsDisabled');
};
